import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { createSchedule } from '../../../data/api';
import validate, { Error, defaultErrors } from './validation';
import { useUserContext } from '../../../hooks/userHook';
import toast from '../../../components/Toast';
import { getStartTime } from '../../../utils/dates';

export default function CreateSchedule() {
  const { user } = useUserContext();

  const [name, setName] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string | null>(getStartTime(null, 30));
  const [endTime, setEndTime] = useState<string | null>(null);
  const [duration, setDuration] = useState<string | null>(null);
  const [published, setPublished] = useState<boolean>(false);
  const [errors, setErrors] = useState<Error>(defaultErrors);
  const [loading, setLoading] = useState<boolean>(false);

  const resetStates = () => {
    setName(null);
    setStartDate(null);
    setEndDate(null);
    setStartTime(null);
    setEndTime(null);
    setDuration(null);
    setErrors(defaultErrors);
  };

  const submitHandler = async (event: any) => {
    event.preventDefault();
    setLoading(true);
    const valuesToValidate = {
      name,
      startDate,
      endDate,
      startTime,
      endTime,
      duration
    };

    const validationResults = validate(valuesToValidate, true);
    const isValid = validationResults?.valid;

    if (!isValid) {
      setErrors(validationResults);
    } else {
      const startDateAsDate = moment(`${startDate} ${startTime}`, 'YYYY/MM/DD h:mm a').utc().format();
      const endDateAsDate = moment(`${endDate} ${endTime}`, 'YYYY/MM/DD h:mm a').utc().format();

      const scheduleRequest = {
        name,
        startDateTimeUTC: startDateAsDate,
        endDateTimeUTC: endDateAsDate,
        duration: parseInt(duration || '30', 10),
        published,
      };

      const result = await createSchedule(scheduleRequest, user?.token);
      console.log('CREATE RESULT', result);
      if (result?.success) {
        toast.success(result.message);
        resetStates();
      } else {
        toast.error(result?.message);
      }

      setLoading(false);
    }
  };

  const doValidation = useCallback(() => {
    const valuesToValidate = {
      name,
      startDate,
      endDate,
      startTime,
      endTime,
      duration
    };

    const validationResults = validate(valuesToValidate);
    setErrors(validationResults);
  }, [name, startDate, endDate, startTime, endTime, duration]);

  useEffect(() => {
    doValidation();
  }, [startTime, endTime, doValidation]);

  return (
    <div id="create-schedule" className="row mb-3">
      <div className="col-md-6 col-lg-4 col-sm-12 mb-4">
        <h1>Create Schedule</h1>

        <Form onSubmit={submitHandler} className="my-4 pb-4">
          <fieldset disabled={loading}>
            <Form.Group className="mb-3" controlId="scheduleName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Name of Event"
                value={name || ''}
                onChange={(event) => setName(event.target.value)}
                onBlur={doValidation}
                isInvalid={!!errors.name}
              />
              <Form.Text className="text-muted">
                optional
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="pt-3 mb-3" controlId="scheduleStartDate">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                placeholder="Start Date"
                value={startDate || ''}
                onChange={(event) => setStartDate(event.target.value)}
                onBlur={doValidation}
                isInvalid={!!errors.startDate}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.startDate}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="scheduleEndDate">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                placeholder="End Date"
                value={endDate || ''}
                onChange={(event) => setEndDate(event.target.value)}
                onBlur={doValidation}
                isInvalid={!!errors.endDate}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.endDate}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="pt-3 mb-3" controlId="scheduleStartTime">
              <Form.Label>Availability Start Time</Form.Label>
              <Form.Control
                type="time"
                placeholder="Start Time"
                value={startTime || ''}
                isInvalid={!!errors.startTime}
                step="900"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setStartTime(event.target.value)}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.startTime}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="scheduleEndTime">
              <Form.Label>Availability End Time</Form.Label>
              <Form.Control
                type="time"
                placeholder="End Time"
                value={endTime || ''}
                isInvalid={!!errors.endTime}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEndTime(event.target.value)}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.endTime}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="pt-3 mb-3" controlId="scheduleDuration">
              <Form.Label>Duration <small>(time per session)</small></Form.Label>
              <Form.Control
                as="select"
                value={duration || ''}
                onChange={(event) => setDuration((event.target as HTMLInputElement).value)}
                isInvalid={!!errors.duration}
                required
              >
                <option value="">Select Duration</option>
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">60 minutes</option>
              </Form.Control>
              <Form.Control.Feedback>
                {errors.duration}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3 pt-4" controlId="scheduleAvailable">
              <Form.Check type="checkbox" aria-label="schedule available">
                <Form.Check.Input type="checkbox" checked={published} onChange={() => setPublished(!published)} />
                <Form.Check.Label className="ms-2">Make Schedule Available</Form.Check.Label>
              </Form.Check>

              <Form.Text className="text-muted">
                by checking this box, it will make this schedule available once submitted
              </Form.Text>
              <hr />
            </Form.Group>

            <div className="d-grid gap-2 mt-4">
              <Button
                size="lg"
                variant="primary"
                type="submit"
                className="mt-4 d-flex justify-content-center align-items-center"
              >
                <span>Add</span>
                {loading && <Spinner animation="border" variant="light" size="sm" className="ms-2" />}
              </Button>
            </div>
          </fieldset>

        </Form>
      </div>
    </div>
  );
}
